/**
 * The application shell: routing, the session, and nothing else.
 *
 * An unauthenticated visitor lands on the sign-in screen whatever URL they
 * arrived at. There is no path through this component that renders a blank page
 * or a raw error: the three things that can go wrong before a screen exists —
 * the API base URL not being configured, a stored token the server no longer
 * honours, and a restore that fails outright — each resolve to a screen with
 * words on it.
 *
 * ## The session, in one place
 *
 * A session is three things held together: the opaque token from
 * `POST /auth/login`, the `Session` document describing who it belongs to, and
 * a local mirror of the server's two expiry clocks. They are kept here rather
 * than in a store so there is exactly one place that can create or destroy a
 * session, and exactly one place that decides what happens when the server says
 * the credential is gone.
 *
 * On boot, a token in `sessionStorage` is verified against `GET /auth/session`
 * before any screen is shown. That single call is the sanctioned use of the
 * route — the contract's own warning is against putting it on a timer, which
 * would slide the idle deadline forever and undo REQ-701. Once. On load. Never
 * on an interval.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createHalyardClient,
  createSessionClock,
  credentialFrom,
  HalyardConfigurationError,
} from "./api/index.ts";
import type {
  AuthenticatedRequest,
  HalyardClient,
  LoginResponseBody,
  SessionBody,
  SessionClock,
  SessionCredential,
} from "./api/index.ts";
import { LoginScreen } from "./components/LoginScreen.tsx";
import { MonitorsScreen } from "./components/MonitorsScreen.tsx";
import { ProblemNotice } from "./components/ProblemNotice.tsx";
import { useRouter } from "./routes/router.tsx";
import {
  clearStoredSession,
  storeOrganisationId,
  storeToken,
  storedOrganisationId,
  storedToken,
} from "./session/session-store.ts";
import { constrainAccent } from "./theme/state-colours.ts";

export const SERVICE_NAME = "Halyard Monitor";

interface ActiveSession {
  readonly credential: SessionCredential;
  readonly session: SessionBody;
  readonly clock: SessionClock;
  readonly organisationId: string | null;
}

/**
 * Which organisation a restored or freshly-issued session should act within.
 *
 * The server's own answer wins: if `session.organisation` is populated, that is
 * the scope it resolved and there is nothing to choose. Otherwise a single
 * membership is unambiguous. A remembered preference is honoured only when it
 * is still one of the caller's memberships — a stale id would earn a 404 that
 * says nothing about whether the organisation exists (REQ-703), so it is
 * dropped rather than sent.
 */
function resolveOrganisation(session: SessionBody, remembered: string | null): string | null {
  if (session.organisation !== null) return session.organisation.id;

  const memberships = session.memberships;
  if (remembered !== null && memberships.some((m) => m.organisation_id === remembered)) {
    return remembered;
  }

  const only = memberships.length === 1 ? memberships[0] : undefined;
  return only === undefined ? null : only.organisation_id;
}

type Boot =
  /** Verifying a stored token, or deciding there is none. Shown for one paint at most. */
  | { readonly kind: "restoring" }
  /** The client itself could not be built — no base URL. Nothing else can happen. */
  | { readonly kind: "misconfigured"; readonly error: unknown }
  | { readonly kind: "ready" };

export function App(): React.JSX.Element {
  const { route, navigate } = useRouter();

  // Built once. `createHalyardClient` throws when the base URL is unset, which
  // is a deployment fault rather than a runtime one — it is caught here so it
  // renders as a screen instead of an unmounted white page.
  const built = useMemo<{ client: HalyardClient } | { error: unknown }>(() => {
    try {
      return { client: createHalyardClient() };
    } catch (error: unknown) {
      return { error };
    }
  }, []);

  const [boot, setBoot] = useState<Boot>(() =>
    "error" in built ? { kind: "misconfigured", error: built.error } : { kind: "restoring" },
  );
  const [active, setActive] = useState<ActiveSession | null>(null);
  const [signInNotice, setSignInNotice] = useState<string | undefined>(undefined);

  const client = "client" in built ? built.client : null;

  const endSession = useCallback(
    (notice?: string) => {
      clearStoredSession();
      setActive(null);
      setSignInNotice(notice);
      navigate("login");
    },
    [navigate],
  );

  // Restore on load. One `GET /auth/session`, then never again on a timer.
  useEffect(() => {
    if (client === null) return;

    const token = storedToken();
    if (token === null) {
      setBoot({ kind: "ready" });
      return;
    }

    const credential: SessionCredential = { token };
    const controller = new AbortController();
    let live = true;

    const request: AuthenticatedRequest = { credential, signal: controller.signal };

    client
      .getSession(request)
      .then((session) => {
        if (!live) return;
        const organisationId = resolveOrganisation(session, storedOrganisationId());
        setActive({
          credential,
          session,
          // The server slid the idle window serving that request, so "now" is
          // the right origin for the local mirror of it.
          clock: createSessionClock(session, new Date()),
          organisationId,
        });
        setBoot({ kind: "ready" });
      })
      .catch(() => {
        if (!live) return;
        // Any failure to restore means there is no usable session. The token is
        // dropped and the person signs in; there is nothing here worth showing
        // them a diagnostic about.
        clearStoredSession();
        setBoot({ kind: "ready" });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  const onSignedIn = useCallback(
    (login: LoginResponseBody) => {
      const credential = credentialFrom(login);
      storeToken(credential.token);
      const organisationId = resolveOrganisation(login.session, storedOrganisationId());
      if (organisationId !== null) storeOrganisationId(organisationId);

      setSignInNotice(undefined);
      setActive({
        credential,
        session: login.session,
        clock: createSessionClock(login.session, new Date()),
        organisationId,
      });
      navigate("monitors");
    },
    [navigate],
  );

  const onChooseOrganisation = useCallback((id: string) => {
    storeOrganisationId(id === "" ? null : id);
    setActive((current) => (current === null ? null : { ...current, organisationId: id }));
  }, []);

  const onSignOut = useCallback(() => {
    if (client === null || active === null) {
      endSession();
      return;
    }

    // Revoke server-side, then forget locally. The local half happens either
    // way: a 401 here means there was no live session to end, which is the
    // outcome that was asked for, and a 403 means the credential was an API key
    // with no session behind it. Neither is worth showing anyone.
    client
      .logOut({ credential: active.credential, clock: active.clock })
      .catch(() => undefined)
      .finally(() => {
        endSession();
      });
  }, [client, active, endSession]);

  const onSessionExpired = useCallback(() => {
    endSession("Your session has ended. Sign in again.");
  }, [endSession]);

  // Signed in but pointed at the sign-in URL — a bookmark, or the back button
  // after signing in. Correct the address rather than showing a form to someone
  // who is already through it.
  const signedIn = active !== null;
  useEffect(() => {
    if (signedIn && route.name === "login") navigate("monitors");
  }, [signedIn, route.name, navigate]);

  // REQ-503: a per-organisation accent is theming *data*, and it is constrained
  // so it can never render as one of the five state colours. The contract
  // carries no accent field yet, so this resolves to the default — the
  // constraint is wired through the one place a value would arrive, rather than
  // being bolted on the day the field appears.
  const accent = useMemo(() => constrainAccent(null).hex, []);

  if (boot.kind === "misconfigured") {
    return (
      <main className="login">
        <div className="login__card">
          <h1 className="login__title">{SERVICE_NAME}</h1>
          <ProblemNotice
            error={
              boot.error instanceof HalyardConfigurationError
                ? boot.error
                : new HalyardConfigurationError(String(boot.error))
            }
          />
        </div>
      </main>
    );
  }

  if (client === null || boot.kind === "restoring") {
    return (
      <main className="login">
        <div className="login__card">
          <h1 className="login__title">{SERVICE_NAME}</h1>
          <div className="loading" role="status">
            <span className="spinner" aria-hidden="true" />
            <p>Restoring your session…</p>
          </div>
        </div>
      </main>
    );
  }

  // Unauthenticated: the sign-in screen, whatever the URL said. This is the
  // branch that makes "never a blank page and never a raw error" true for every
  // path, not only for the two the router names.
  if (active === null) {
    return <LoginScreen client={client} onSignedIn={onSignedIn} notice={signInNotice} />;
  }

  return (
    <MonitorsScreen
      client={client}
      credential={active.credential}
      session={active.session}
      clock={active.clock}
      organisationId={active.organisationId}
      onChooseOrganisation={onChooseOrganisation}
      onSignOut={onSignOut}
      onSessionExpired={onSessionExpired}
      accent={accent}
    />
  );
}
